"use client";

import { c as _c } from "react/compiler-runtime";
import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "next/navigation";
import { useSocket } from "@/hooks/use-socket";
import { SOCKET_EVENTS } from "@adfdev/shared/socket/events";
import { getOrCreateVisitorId } from "@/lib/visitor";
import type { Socket } from "socket.io-client";
interface Message {
  id: string;
  senderType: "admin" | "visitor";
  content: string;
  createdAt: string;
  isRead: boolean;
}
interface VisitorSession {
  visitorSessionId: string;
  sessionTitle: string;
  portal: string;
  lastMessage: string | null;
  lastActive: string;
}
interface ChatContextValue {
  socket: Socket | null;
  isConnected: boolean;
  visitorId: string;
  visitorSessions: VisitorSession[];
  activeSessionId: string | null;
  messages: Message[];
  unreadCount: number;
  isAdminOnline: boolean;
  isAdminTyping: boolean;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  setActiveSessionId: (id: string) => void;
  sendMessage: (content: string) => void;
  emitTyping: () => void;
}
const ChatContext = createContext<ChatContextValue | null>(null);
export function useChatContext() {
  const ctx = useContext(ChatContext);
  if (!ctx) {
    throw new Error("useChatContext must be used within ChatProvider");
  }
  return ctx;
}
export function ChatProvider(t0) {
  const $ = _c(41);
  const {
    children
  } = t0;
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams.get("ref");
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const ref = t1;
  const {
    socket,
    isConnected
  } = useSocket();
  const [visitorId, setVisitorId] = useState("");
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [visitorSessions, setVisitorSessions] = useState(t2);
  const [activeSessionId, setActiveSessionId] = useState(null);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [messages, setMessages] = useState(t3);
  const [isAdminOnline, setIsAdminOnline] = useState(true);
  const [isAdminTyping, setIsAdminTyping] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [initialized, setInitialized] = useState(false);
  let t4;
  let t5;
  if ($[4] !== initialized || $[5] !== ref) {
    t4 = () => {
      if (initialized) {
        return;
      }
      const init = async () => {
        const cookieId = getOrCreateVisitorId();
        if (!cookieId) {
          return;
        }
        const identifyRes = await fetch("/api/chat/identify", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            cookieId,
            ref
          })
        });
        if (!identifyRes.ok) {
          console.error("[chat] identify failed:", identifyRes.status);
          return;
        }
        const identifyData = await identifyRes.json();
        setVisitorId(identifyData.visitorId);
        const sessionsRes = await fetch(`/api/chat/sessions?cookieId=${cookieId}`);
        if (!sessionsRes.ok) {
          console.error("[chat] sessions failed:", sessionsRes.status);
          return;
        }
        const sessions = await sessionsRes.json();
        setVisitorSessions(sessions);
        if (identifyData.visitorSessionId) {
          setActiveSessionId(identifyData.visitorSessionId);
          if (ref) {
            setIsOpen(true);
          }
        } else {
          if (sessions.length > 0) {
            setActiveSessionId(sessions[0].visitorSessionId);
          }
        }
        setInitialized(true);
      };
      init();
    };
    t5 = [ref, initialized];
    $[4] = initialized;
    $[5] = ref;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[8] !== activeSessionId) {
    t6 = () => {
      if (!activeSessionId) {
        return;
      }
      fetch(`/api/chat/messages?visitorSessionId=${activeSessionId}`).then(_temp).then(setMessages);
    };
    t7 = [activeSessionId];
    $[8] = activeSessionId;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[11] !== activeSessionId || $[12] !== socket || $[13] !== visitorId) {
    t8 = () => {
      if (!socket || !activeSessionId || !visitorId) {
        return;
      }
      socket.emit(SOCKET_EVENTS.JOIN_SESSION, {
        visitorSessionId: activeSessionId,
        senderType: "visitor"
      });
      const handleNewMessage = msg => {
        setMessages(prev => [...prev, msg]);
      };
      const handleTypingStart = data => {
        if (data.visitorSessionId === activeSessionId && data.senderType === "admin") {
          setIsAdminTyping(true);
        }
      };
      const handleTypingStop = data_0 => {
        if (data_0.visitorSessionId === activeSessionId && data_0.senderType === "admin") {
          setIsAdminTyping(false);
        }
      };
      const handleAdminPresence = data_1 => {
        setIsAdminOnline(data_1.isOnline);
      };
      const handleMessagesRead = data_2 => {
        if (data_2.visitorSessionId === activeSessionId) {
          setMessages(_temp3);
        }
      };
      socket.on(SOCKET_EVENTS.NEW_MESSAGE, handleNewMessage);
      socket.on(SOCKET_EVENTS.TYPING_START, handleTypingStart);
      socket.on(SOCKET_EVENTS.TYPING_STOP, handleTypingStop);
      socket.on(SOCKET_EVENTS.ADMIN_PRESENCE, handleAdminPresence);
      socket.on(SOCKET_EVENTS.MESSAGES_READ, handleMessagesRead);
      return () => {
        socket.off(SOCKET_EVENTS.NEW_MESSAGE, handleNewMessage);
        socket.off(SOCKET_EVENTS.TYPING_START, handleTypingStart);
        socket.off(SOCKET_EVENTS.TYPING_STOP, handleTypingStop);
        socket.off(SOCKET_EVENTS.ADMIN_PRESENCE, handleAdminPresence);
        socket.off(SOCKET_EVENTS.MESSAGES_READ, handleMessagesRead);
        socket.emit(SOCKET_EVENTS.LEAVE_SESSION, {
          visitorSessionId: activeSessionId,
          senderType: "visitor"
        });
      };
    };
    t9 = [socket, activeSessionId, visitorId];
    $[11] = activeSessionId;
    $[12] = socket;
    $[13] = visitorId;
    $[14] = t8;
    $[15] = t9;
  } else {
    t8 = $[14];
    t9 = $[15];
  }
  useEffect(t8, t9);
  let t10;
  if ($[16] !== activeSessionId || $[17] !== socket) {
    t10 = async content => {
      if (!socket) {
        return;
      }
      if (!activeSessionId) {
        const cookieId_0 = getOrCreateVisitorId();
        if (!cookieId_0) {
          return;
        }
        const res = await fetch("/api/chat/create-direct", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            cookieId: cookieId_0,
            firstMessage: content
          })
        });
        if (!res.ok) {
          return;
        }
        const data_3 = await res.json();
        setActiveSessionId(data_3.visitorSessionId);
        if (data_3.message) {
          setMessages([data_3.message]);
        }
        socket.emit(SOCKET_EVENTS.JOIN_SESSION, {
          visitorSessionId: data_3.visitorSessionId,
          senderType: "visitor"
        });
        return;
      }
      socket.emit(SOCKET_EVENTS.SEND_MESSAGE, {
        visitorSessionId: activeSessionId,
        senderType: "visitor",
        content
      });
    };
    $[16] = activeSessionId;
    $[17] = socket;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const sendMessage = t10;
  let t11;
  if ($[19] !== activeSessionId || $[20] !== socket) {
    t11 = () => {
      if (!socket || !activeSessionId) {
        return;
      }
      socket.emit(SOCKET_EVENTS.TYPING_START, {
        visitorSessionId: activeSessionId,
        senderType: "visitor"
      });
    };
    $[19] = activeSessionId;
    $[20] = socket;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const emitTyping = t11;
  let t12;
  if ($[22] !== isOpen || $[23] !== messages) {
    t12 = isOpen ? 0 : messages.filter(_temp4).length;
    $[22] = isOpen;
    $[23] = messages;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const unreadCount = t12;
  let t13;
  if ($[25] !== activeSessionId || $[26] !== emitTyping || $[27] !== isAdminOnline || $[28] !== isAdminTyping || $[29] !== isConnected || $[30] !== isOpen || $[31] !== messages || $[32] !== sendMessage || $[33] !== socket || $[34] !== unreadCount || $[35] !== visitorId || $[36] !== visitorSessions) {
    t13 = {
      socket,
      isConnected,
      visitorId,
      visitorSessions,
      activeSessionId,
      messages,
      unreadCount,
      isAdminOnline,
      isAdminTyping,
      isOpen,
      setIsOpen,
      setActiveSessionId,
      sendMessage,
      emitTyping
    };
    $[25] = activeSessionId;
    $[26] = emitTyping;
    $[27] = isAdminOnline;
    $[28] = isAdminTyping;
    $[29] = isConnected;
    $[30] = isOpen;
    $[31] = messages;
    $[32] = sendMessage;
    $[33] = socket;
    $[34] = unreadCount;
    $[35] = visitorId;
    $[36] = visitorSessions;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== children || $[39] !== t13) {
    t14 = <ChatContext.Provider value={t13}>{children}</ChatContext.Provider>;
    $[38] = children;
    $[39] = t13;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  return t14;
}
function _temp4(m_0) {
  return m_0.senderType === "admin" && !m_0.isRead;
}
function _temp3(prev_0) {
  return prev_0.map(_temp2);
}
function _temp2(m) {
  return {
    ...m,
    isRead: true
  };
}
function _temp(r) {
  return r.json();
}